import React, { useState, useRef } from "react";
import { Row, Col, Alert, Card } from "react-bootstrap";
import { motion } from "framer-motion";
import {
  FaCloudUploadAlt,
  FaCheckCircle,
  FaTimes,
  FaIdCard,
  FaExclamationTriangle,
} from "react-icons/fa";
import { FileUploadService } from "../../../../utils/fileUploadService";

const IdentityVerificationStep = ({
  formData,
  updateFormData,
  errors,
  setErrors,
  nextStep,
  prevStep,
}) => {
  const [uploading, setUploading] = useState(false);
  const [ocrData, setOcrData] = useState(null);

  const fileInputRef = useRef(null);

  const identity = formData.identity || {};

  // Handle file upload for ID documents
  const handleFileUpload = async (event, type) => {
    const file = event.target.files[0];
    if (!file) return;

    setUploading(true);

    try {
      // Generate session ID if not exists (for file organization)
      const sessionId =
        formData.sessionId ||
        `kyc_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;

      // If this is the first upload, store the session ID
      if (!formData.sessionId) {
        updateFormData("general", { sessionId });
      }

      // Upload file to Supabase Storage
      const uploadResult = await FileUploadService.uploadFile(
        file,
        sessionId,
        type
      );

      if (!uploadResult.success) {
        setErrors((prev) => ({
          ...prev,
          [type]: uploadResult.error,
        }));
        return;
      }

      // Simulate OCR processing
      if (type === "idDocument") {
        await simulateOCR(file);
      }

      // Store file metadata (NOT base64 data)
      updateFormData("identity", {
        ...identity,
        [type]: {
          path: uploadResult.data.path,
          publicUrl: uploadResult.data.publicUrl,
          fileName: uploadResult.data.fileName,
          fileSize: uploadResult.data.fileSize,
          fileType: uploadResult.data.fileType,
          uploadedAt: uploadResult.data.uploadedAt,
        },
        [`${type}Name`]: uploadResult.data.fileName,
        [`${type}Size`]: uploadResult.data.fileSize,
        [`${type}Type`]: uploadResult.data.fileType,
      });

      // Clear any existing errors
      setErrors((prev) => {
        const newErrors = { ...prev };
        delete newErrors[type];
        return newErrors;
      });

      console.log("File uploaded successfully:", uploadResult.data);
    } catch (error) {
      console.error("Upload error:", error);
      setErrors((prev) => ({
        ...prev,
        [type]: "Failed to upload file. Please try again.",
      }));
    } finally {
      setUploading(false);
    }
  };

  // Simulate OCR processing
  const simulateOCR = async (file) => {
    // Simulate API call delay
    await new Promise((resolve) => setTimeout(resolve, 2000));

    // Mock OCR results
    const mockOcrData = {
      documentType: "passport",
      documentNumber: "A12345678",
      expiryDate: "2030-12-31",
      issueDate: "2020-01-01",
      nationality: "United States",
      confidence: 0.95,
    };

    setOcrData(mockOcrData);
  };

  // Remove uploaded file
  const removeFile = async (type) => {
    const fileData = identity[type];

    // Delete file from Supabase Storage if it exists
    if (fileData && fileData.path) {
      try {
        await FileUploadService.deleteFile(fileData.path);
        console.log("File deleted from storage:", fileData.path);
      } catch (error) {
        console.error("Error deleting file from storage:", error);
      }
    }

    // Remove from form data
    updateFormData("identity", {
      ...identity,
      [type]: null,
      [`${type}Name`]: null,
      [`${type}Size`]: null,
      [`${type}Type`]: null,
    });

    if (type === "idDocument") {
      setOcrData(null);
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5 }}
    >
      <Row>
        <Col lg={8} className="mx-auto">
          {/* ID Document Upload */}
          <Card className="mb-4 border-0 bg-dark-subtle">
            <Card.Body className="p-4">
              <div className="d-flex align-items-center mb-3">
                <FaIdCard className="text-primary me-2" />
                <h5 className="mb-0">Government-Issued ID Document</h5>
              </div>

              <p className="text-muted mb-3">
                Upload a clear photo of your passport, driver's license, or
                national ID card. Both sides may be required.
              </p>

              {!identity.idDocument || !identity.idDocumentName ? (
                <div className="upload-zone text-center p-5 border border-2 border-dashed rounded-3">
                  <FaCloudUploadAlt size={48} className="text-muted mb-3" />
                  <h6>Drag & drop your ID document here</h6>
                  <p className="text-muted small mb-3">
                    or click to browse files
                  </p>
                  <input
                    type="file"
                    ref={fileInputRef}
                    onChange={(e) => handleFileUpload(e, "idDocument")}
                    accept="image/*,.pdf"
                    className="d-none"
                  />
                  <button
                    type="button"
                    className="btn btn-outline-primary"
                    onClick={() => fileInputRef.current?.click()}
                    disabled={uploading}
                  >
                    {uploading ? "Uploading..." : "Choose File"}
                  </button>
                  <div className="small text-muted mt-2">
                    Supported formats: JPEG, PNG, PDF (max 5MB)
                  </div>
                </div>
              ) : (
                <div className="uploaded-file d-flex align-items-center justify-content-between p-3 bg-success bg-opacity-10 rounded-3">
                  <div className="d-flex align-items-center">
                    <FaCheckCircle className="text-success me-2" />
                    <div>
                      <div className="fw-medium">{identity.idDocumentName}</div>
                      <small className="text-muted">
                        {(identity.idDocumentSize / 1024).toFixed(1)} KB
                      </small>
                    </div>
                  </div>
                  <button
                    type="button"
                    className="btn btn-sm btn-outline-danger"
                    onClick={() => removeFile("idDocument")}
                  >
                    <FaTimes />
                  </button>
                </div>
              )}

              {errors.idDocument && (
                <Alert variant="danger" className="mt-3 mb-0">
                  <FaExclamationTriangle className="me-2" />
                  {errors.idDocument}
                </Alert>
              )}

              {/* OCR Results */}
              {ocrData && (
                <Alert variant="info" className="mt-3">
                  <FaCheckCircle className="me-2" />
                  <strong>Document automatically verified!</strong>
                  <div className="mt-2 small">
                    <div>Document Type: {ocrData.documentType}</div>
                    <div>Document Number: {ocrData.documentNumber}</div>
                    <div>Expiry Date: {ocrData.expiryDate}</div>
                    <div>
                      Confidence: {(ocrData.confidence * 100).toFixed(1)}%
                    </div>
                  </div>
                </Alert>
              )}
            </Card.Body>
          </Card>

          {/* Security Notice */}
          <Alert variant="info" className="mb-4">
            <FaCheckCircle className="me-2" />
            <strong>Your data is secure:</strong> All documents are encrypted
            and stored securely. We use advanced OCR technology to verify your
            identity quickly and safely.
          </Alert>
        </Col>
      </Row>
    </motion.div>
  );
};

export default IdentityVerificationStep;
